import type { BoundaryCardData } from '../types'

interface Props {
  data: BoundaryCardData
  onPrint?: () => void
  onArchivePng?: () => void
  onDownloadSvg?: () => void
  archivedUrl?: string
  isArchiving?: boolean
}

export function BoundaryCard({ data, onPrint, onArchivePng, onDownloadSvg, archivedUrl, isArchiving }: Props) {
  const date = data.closed_at || new Date().toISOString().slice(0, 10)

  // Pure SVG card — crisp, archival, printable, exportable
  const svgContent = `
<svg xmlns="http://www.w3.org/2000/svg" width="720" height="920" viewBox="0 0 720 920">
  <rect width="720" height="920" fill="#fdfcf8"/>
  <rect x="28" y="28" width="664" height="864" fill="none" stroke="#2c3e50" stroke-width="2"/>
  
  <!-- header -->
  <text x="360" y="72" font-family="Georgia, serif" font-size="13" letter-spacing="3" fill="#6b7779" text-anchor="middle">BOUNDARY CLARITY CARD</text>
  <text x="360" y="94" font-family="Georgia, serif" font-size="11" fill="#6b7779" text-anchor="middle">${data.ledger_short_id}  ·  ${date}</text>
  
  <line x1="60" y1="112" x2="660" y2="112" stroke="#c9c6bb" stroke-width="1"/>

  <!-- project -->
  <text x="60" y="145" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">PROJECT</text>
  <text x="60" y="170" font-family="Georgia, serif" font-size="17" fill="#1c2526">${escapeXml(data.project_name)}</text>
  
  <text x="60" y="205" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">INTENTION (IN PUBLIC TERMS)</text>
  <text x="60" y="228" font-family="Georgia, serif" font-size="13.5" fill="#1c2526">
    ${wrapText(data.intention, 82).map((l, i) => `<tspan x="60" dy="${i === 0 ? 0 : 18}">${escapeXml(l)}</tspan>`).join('')}
  </text>

  <!-- stated values + evidence -->
  <text x="60" y="295" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">STATED VALUES + CURRENT EVIDENCE SUPPORT</text>
  ${data.stated_values.map((v, i) => {
    const y = 318 + i * 52
    return `
      <text x="60" y="${y}" font-family="Georgia, serif" font-size="13" fill="#1c2526">${escapeXml(v.text)}</text>
      <text x="60" y="${y + 18}" font-family="system-ui, sans-serif" font-size="11" fill="#4a5557">support: ${v.support}</text>
    `
  }).join('')}

  <!-- revealed -->
  <text x="60" y="530" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">VALUES REVEALED FROM PRIOR WORK (ONE CONCRETE EXAMPLE)</text>
  <text x="60" y="550" font-family="Georgia, serif" font-size="12.5" fill="#1c2526">
    ${wrapText(data.revealed_note || '—', 78).map((l, i) => `<tspan x="60" dy="${i === 0 ? 0 : 16}">${escapeXml(l)}</tspan>`).join('')}
  </text>

  <!-- discomfort -->
  <text x="60" y="615" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">DISCOMFORT (WHERE THE PLAN FEELS THIN)</text>
  <text x="60" y="635" font-family="Georgia, serif" font-size="12.5" fill="#1c2526">
    ${wrapText(data.discomfort_note || '—', 78).map((l, i) => `<tspan x="60" dy="${i === 0 ? 0 : 16}">${escapeXml(l)}</tspan>`).join('')}
  </text>

  <!-- boundaries -->
  <text x="60" y="705" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">BOUNDARIES ENTERED</text>
  ${data.boundaries.slice(0, 4).map((b, i) => 
    `<text x="60" y="${725 + i * 18}" font-family="Georgia, serif" font-size="12" fill="#1c2526">• ${escapeXml(b)}</text>`
  ).join('')}

  <!-- bridge -->
  <text x="60" y="815" font-family="system-ui, sans-serif" font-size="9" letter-spacing="1.5" fill="#6b7779">BRIDGE-TO-ACTION (NEXT 48–72H) + WHAT I WILL DECLINE</text>
  <text x="60" y="835" font-family="Georgia, serif" font-size="12.5" fill="#1c2526">Action: ${escapeXml(data.bridge_action)}</text>
  <text x="60" y="855" font-family="Georgia, serif" font-size="12.5" fill="#1c2526">Decline: ${escapeXml(data.not_doing)}</text>

  <!-- footer -->
  <text x="360" y="892" font-family="system-ui, sans-serif" font-size="8.5" fill="#6b7779" text-anchor="middle">This card records what I knew and chose at the time of closing. It is not a promise of delivery.</text>
</svg>`.trim()

  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`

  return (
    <div className="boundary-card mx-auto w-full max-w-[720px] p-8 md:p-10 text-[15px] text-ink" id="boundary-card">
      <div className="flex justify-between items-start mb-6 no-print">
        <div>
          <div className="label tracking-[2px]">BOUNDARY CLARITY CARD</div>
          <div className="text-xs text-ink-light mt-0.5 font-mono">{data.ledger_short_id} · {date}</div>
        </div>
        <div className="flex gap-2 no-print">
          {onPrint && (
            <button onClick={onPrint} className="ghost px-3 py-1.5 text-xs rounded">Print</button>
          )}
          {onDownloadSvg && (
            <button onClick={onDownloadSvg} className="ghost px-3 py-1.5 text-xs rounded">SVG</button>
          )}
          {onArchivePng && (
            <button 
              onClick={onArchivePng} 
              disabled={isArchiving}
              className="ghost px-3 py-1.5 text-xs rounded disabled:opacity-50"
            >
              {isArchiving ? 'Archiving…' : 'Archive PNG to R2'}
            </button>
          )}
        </div>
      </div>

      {/* Visual SVG render */}
      <div className="flex justify-center">
        <img 
          src={svgDataUrl} 
          alt={`Boundary Clarity Card for ${data.project_name}`} 
          className="w-full max-w-[640px] h-auto border border-[#c9c6bb] bg-white shadow-sm" 
        />
      </div>

      {archivedUrl && (
        <div className="mt-4 text-center no-print">
          <a 
            href={archivedUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs underline text-accent hover:text-ink"
          >
            View archived PNG in R2 →
          </a>
        </div>
      )}

      <div className="mt-8 text-[11px] text-ink-light leading-snug border-t border-[#e8e6df] pt-4 no-print">
        This is a faithful record of what you stated, what evidence you had, the boundaries you set, and the single bridge action you chose. 
        Future-you (via the private check-in if you requested one) is the only audience that matters for the next weighing.
      </div>
    </div>
  )
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let current = ''
  for (const w of words) {
    if ((current + ' ' + w).trim().length > maxChars) {
      if (current) lines.push(current)
      current = w
    } else {
      current = (current + ' ' + w).trim()
    }
  }
  if (current) lines.push(current)
  return lines.length ? lines : ['']
}
